import { useAppUser } from '../admin/AppUserContext';
import { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import {
  ArrowLeft,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  FileText,
  HelpCircle,
  LayoutDashboard,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Sparkles,
  Target,
  Users,
  X,
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import { companiesApi, type Company } from '../services/api';
import { CLOUDFLARE_STATIC_ASSETS } from '../config/cloudflareAssets';
import { CompanyWorkspaceContext } from './companyWorkspaceContext';
import { getCompanySwitchPath, getCompanyWorkspaceSection } from '../utils/companyWorkspace';
import '../pages/DashboardPage.css';
import './CompanyWorkspaceLayout.css';

const sections = [
  { id: 'overview', label: 'Company overview', path: '', icon: LayoutDashboard },
  { id: 'context', label: 'Company context', path: '/context', icon: Building2 },
  { id: 'ai-plan', label: 'AI strategic plans', path: '/ai-plan', icon: Sparkles },
  { id: 'strategy', label: 'Strategic plan', path: '/strategy', icon: Target },
  { id: 'people', label: 'People & structure', path: '/organization', icon: Users },
  { id: 'execution', label: 'Execution', path: '/execution', icon: CheckCircle2 },
  { id: 'reports', label: 'Reports', path: '/visual-report', icon: FileText },
  { id: 'settings', label: 'Company settings', path: '/admin', icon: Settings },
] as const;

export default function CompanyWorkspaceLayout() {
  const { companyId } = useParams<{ companyId: string }>();
  const { user, isSupportSession } = useAppUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('stratplan.company-sidebar-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((previous) => {
      try {
        localStorage.setItem('stratplan.company-sidebar-collapsed', String(!previous));
      } catch {
        /* Navigation remains usable without local storage. */
      }
      return !previous;
    });
  };
  const activeSection = getCompanyWorkspaceSection(location.pathname);
  const companyRole = company?.role || companies.find((item) => item.id === companyId)?.role;
  const canManage = companyRole === 'owner' || companyRole === 'admin';
  const sectionLabel =
    sections.find((section) => section.id === activeSection)?.label || 'Company overview';

  useEffect(() => {
    let cancelled = false;
    companiesApi
      .list()
      .then((response) => {
        if (!cancelled && response.success && response.data) setCompanies(response.data);
      })
      .catch(() => {
        // The page has its own error/retry UI; navigation remains usable if the switcher cannot load.
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    setCompany(null);
    if (companyId) {
      companiesApi
        .get(companyId)
        .then((response) => {
          if (!cancelled && response.success && response.data) setCompany(response.data);
        })
        .catch(() => {
          // Keep the workspace available while its child page explains any load failure.
        });
    }
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const activeCompany = company || companies.find((item) => item.id === companyId) || null;
  const updateCompanyAppearance = useCallback(
    (appearance: Pick<Company, 'name' | 'logo'>) => {
      setCompany((current) =>
        current?.id === companyId &&
        (current.name !== appearance.name || current.logo !== appearance.logo)
          ? { ...current, ...appearance }
          : current
      );
      setCompanies((current) =>
        current.some(
          (item) =>
            item.id === companyId &&
            (item.name !== appearance.name || item.logo !== appearance.logo)
        )
          ? current.map((item) => (item.id === companyId ? { ...item, ...appearance } : item))
          : current
      );
    },
    [companyId]
  );
  const navigation = (
    <>
      <Link to="/dashboard" className="company-workspace-back" title="All companies">
        <ArrowLeft size={16} /> <span>All companies</span>
      </Link>
      <div className="company-workspace-identity">
        <span className="dashboard-company-avatar" aria-hidden="true">
          {activeCompany?.logo ? (
            <img src={activeCompany.logo} alt="" />
          ) : (
            activeCompany?.name.slice(0, 2).toUpperCase() || <Building2 size={19} />
          )}
        </span>
        <div>
          <strong>{activeCompany?.name || 'Company workspace'}</strong>
          <span>{activeCompany?.industry || 'Your connected strategy'}</span>
        </div>
      </div>
      <p className="dashboard-nav-label">Company workspace</p>
      <nav aria-label="Company navigation" className="dashboard-nav company-workspace-nav">
        {sections
          .filter((section) => section.id !== 'settings' || canManage)
          .map((section) => (
            <Link
              key={section.id}
              title={section.label}
              to={`/company/${companyId}${section.path}`}
              className={activeSection === section.id ? 'is-active' : undefined}
              aria-current={activeSection === section.id ? 'page' : undefined}
            >
              <section.icon size={19} /> <span>{section.label}</span>
            </Link>
          ))}
      </nav>
      <div className="dashboard-sidebar-bottom">
        <div className="dashboard-sidebar-tip">
          <span className="dashboard-icon purple">
            <Sparkles size={19} />
          </span>
          <h3>Keep your strategy connected.</h3>
          <p>Build direction, align your people, and turn priorities into progress.</p>
          <Link to={`/company/${companyId}/strategy`}>
            Explore your plan <ArrowUpRight size={14} />
          </Link>
        </div>
        <Link to="/contact" className="dashboard-help-link">
          <HelpCircle size={18} /> Help & support
        </Link>
      </div>
    </>
  );

  return (
    <CompanyWorkspaceContext.Provider
      value={{ company: activeCompany, companies, updateCompanyAppearance }}
    >
      <div
        className={`dashboard-workspace company-workspace ${sidebarCollapsed ? 'company-workspace--collapsed' : ''}`}
      >
        <a href="#company-workspace-main" className="dashboard-skip-link">
          Skip to company content
        </a>
        <aside id="company-desktop-sidebar" className="dashboard-sidebar">
          <Link to="/dashboard" className="dashboard-brand" aria-label="StratPlan dashboard">
            <img
              src={
                sidebarCollapsed
                  ? CLOUDFLARE_STATIC_ASSETS.LOGO_ICON
                  : CLOUDFLARE_STATIC_ASSETS.LOGO_LIGHT
              }
              alt="StratPlan"
            />
          </Link>
          <div className="dashboard-sidebar-body">{navigation}</div>
        </aside>
        <div className="dashboard-page">
          <header className="dashboard-topbar company-workspace-topbar">
            <div className="dashboard-topbar-title">
              <button
                type="button"
                className="dashboard-mobile-toggle dashboard-icon-button"
                aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={menuOpen}
                aria-controls="company-mobile-navigation"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? <X size={21} /> : <Menu size={21} />}
              </button>
              <button
                type="button"
                className="company-sidebar-toggle dashboard-icon-button"
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-expanded={!sidebarCollapsed}
                aria-controls="company-desktop-sidebar"
                onClick={toggleSidebar}
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />}
              </button>
              <div className="company-workspace-switcher">
                <label htmlFor="company-workspace-switch">Company</label>
                <select
                  id="company-workspace-switch"
                  aria-label="Switch company"
                  value={companyId || ''}
                  onChange={(event) => {
                    const targetCompany = companies.find((item) => item.id === event.target.value);
                    const targetCanManage =
                      targetCompany?.capabilities?.manageMembers ??
                      (targetCompany?.role === 'owner' || targetCompany?.role === 'admin');
                    navigate(
                      activeSection === 'settings' && !targetCanManage
                        ? `/company/${event.target.value}`
                        : getCompanySwitchPath(location.pathname, event.target.value, location.hash)
                    );
                  }}
                  disabled={!companies.length}
                >
                  {!companies.some((item) => item.id === companyId) && (
                    <option value={companyId || ''}>
                      {activeCompany?.name || 'Company workspace'}
                    </option>
                  )}
                  {companies.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <span className="company-workspace-section">
                <ChevronRight size={14} /> {sectionLabel}
              </span>
            </div>
            <div className="dashboard-topbar-actions">
              <NotificationBell />
              <div className="dashboard-user-menu">
                <div className="dashboard-user-name">
                  <strong>{user?.fullName || user?.firstName || 'Your account'}</strong>
                  <span>Company workspace</span>
                </div>
                {!isSupportSession && (
                  <UserButton
                    appearance={{ elements: { avatarBox: 'h-9 w-9' } }}
                    userProfileMode="navigation"
                    userProfileUrl="/user-profile"
                    afterSignOutUrl="/"
                  >
                    <UserButton.MenuItems>
                      <UserButton.Link
                        label="Subscriptions"
                        labelIcon={<CreditCard size={16} />}
                        href="/billing"
                      />
                    </UserButton.MenuItems>
                  </UserButton>
                )}
              </div>
            </div>
          </header>
          {menuOpen && (
            <div id="company-mobile-navigation" className="dashboard-mobile-navigation">
              {navigation}
            </div>
          )}
          <main id="company-workspace-main" className="company-workspace-main" tabIndex={-1}>
            <Outlet key={companyId} />
          </main>
        </div>
      </div>
    </CompanyWorkspaceContext.Provider>
  );
}
